import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CurriculumHub from './CurriculumHub';
import CurriculumLibraryView from './CurriculumLibraryView';
import CurriculumTextbookDetail from './CurriculumTextbookDetail';
import { getLibraryBySlug } from './curriculumLibrariesService';
import { createCurriculumService } from './curriculumService';
import './Curriculum.css';

function useLibraryBySlug(slug) {
  const [state, setState] = useState({ loading: true, library: null });

  useEffect(() => {
    let active = true;
    setState({ loading: true, library: null });
    getLibraryBySlug(slug)
      .then((library) => {
        if (active) setState({ loading: false, library });
      })
      .catch(() => {
        if (active) setState({ loading: false, library: null });
      });
    return () => {
      active = false;
    };
  }, [slug]);

  return state;
}

function NotFound({ message, basePath, navigate }) {
  return (
    <div className="apcLoading" style={{ flexDirection: 'column', gap: 12 }}>
      <div>{message}</div>
      <button type="button" className="apcBtn apcBtn--ghost" onClick={() => navigate(basePath)}>
        Back to Curriculum
      </button>
    </div>
  );
}

function LibraryRoute({ slug, basePath, canDelete, navigate }) {
  const { loading, library } = useLibraryBySlug(slug);

  if (loading) return <div className="apcLoading">Loading library...</div>;
  if (!library) return <NotFound message="Library not found." basePath={basePath} navigate={navigate} />;

  return (
    <CurriculumLibraryView
      key={library.id}
      library={library}
      canDelete={canDelete}
      onOpenTextbook={(textbookId) => navigate(`${basePath}/${slug}/${textbookId}`)}
    />
  );
}

function TextbookRoute({ slug, textbookId, basePath, canDelete, navigate }) {
  const { loading, library } = useLibraryBySlug(slug);
  const [resolving, setResolving] = useState(true);
  const [categoryId, setCategoryId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (!library) return undefined;
    let active = true;
    setResolving(true);
    const service = createCurriculumService(library);
    service
      .resolveTextbookLocation(textbookId)
      .then((location) => {
        if (active) {
          setCategoryId(location?.categoryId || null);
          setResolving(false);
        }
      })
      .catch(() => {
        if (active) {
          setCategoryId(null);
          setResolving(false);
        }
      });
    return () => {
      active = false;
    };
  }, [library, textbookId]);

  if (loading || (library && resolving)) return <div className="apcLoading">Loading textbook...</div>;
  if (!library) return <NotFound message="Library not found." basePath={basePath} navigate={navigate} />;
  if (!categoryId) {
    return (
      <NotFound message="Textbook not found." basePath={`${basePath}/${slug}`} navigate={navigate} />
    );
  }

  return (
    <CurriculumTextbookDetail
      key={`${library.id}/${textbookId}`}
      library={library}
      categoryId={categoryId}
      textbookId={textbookId}
      onBack={() => navigate(`${basePath}/${slug}`)}
      isEditMode={isEditMode}
      onToggleEditMode={() => setIsEditMode((prev) => !prev)}
      canToggleEditMode
      canDelete={canDelete}
    />
  );
}

// Single entry point for the curriculum feature, mounted inside the dashboard
// panel for every /curriculum* path. Picks Hub / Library / Textbook from the URL
// so libraries and textbooks have real, shareable, refresh-safe addresses.
export default function CurriculumApp({ basePath = '/curriculum', permissions = {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const canDelete = !!permissions.canDelete;

  const rest = location.pathname
    .slice(basePath.length)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  const segments = rest ? rest.split('/') : [];
  const [slug, textbookId] = segments;

  if (!slug) {
    return <CurriculumHub canDelete={canDelete} onOpenLibrary={(s) => navigate(`${basePath}/${s}`)} />;
  }

  if (!textbookId) {
    return <LibraryRoute slug={slug} basePath={basePath} canDelete={canDelete} navigate={navigate} />;
  }

  return (
    <TextbookRoute slug={slug} textbookId={textbookId} basePath={basePath} canDelete={canDelete} navigate={navigate} />
  );
}
